import { Button, ActionMenu, ActionList, Spinner, Dialog } from '@primer/react';
import { useState } from 'react';
import { PlusIcon, KebabHorizontalIcon, TrashIcon, PencilIcon } from '@primer/octicons-react';
import ResetIcon from '@streamlinehq/streamlinehq/img/manual/reset-streamline-core.svg';
import { UpdateStreamlineCoreLine as UpdateIcon } from '@upright/ui/icons';
import styled from 'styled-components';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { TrackingId } from '@upright/ui/integrations/tracking-ids';
import ButtonWithTooltipWhenDisabled from '@upright/ui/common/generic/components/buttons/disabled-button-with-tooltip';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { ProductEditingContext } from './types';
import { INPUT_GUIDANCE } from './input-guidance';
import { SegmentViewMode } from './segment-view-preferences';
import { useHasProductAttributeAccess } from './product-attribute-access';

interface ProductControlButtonsProps {
  showMatching: boolean;
  onToggleMatching: () => void;
  showAttributes: boolean;
  onToggleAttributes: () => void;
  /**
   * List / Hierarchy view-mode toggle. Optional: the segmented control is
   * rendered only when both `viewMode` and `onViewModeChange` are provided.
   * Hosts pass them only with segment-editor access (`useHasProductSegmentsAccess`):
   * the dashboard "Products & services" page, the DD wizard's and the consultant
   * company-creation wizard's review steps, and the chat product set card (PLT-7586).
   */
  viewMode?: SegmentViewMode;
  onViewModeChange?: (mode: SegmentViewMode) => void;
  onAddProduct: () => void;
  /**
   * Create a new top-level segment (PLT-5691). Provided only by the gated segment
   * editor; when omitted the "Add segment" toolbar button is not rendered (PLT-5707).
   */
  onAddBusinessUnit?: () => void;
  onClearAll: () => void;
  onResetChanges: () => void;
  onUpdateClick: () => void;
  /**
   * Label of the back-compat "Recompute results" button, which `onUpdateClick` runs. Only the chat
   * side panel overrides it: edits there autosave, and the button approves the set through the chat
   * (PLT-7087), so "Recompute" read as the way to keep edits.
   */
  updateButtonLabel?: string;
  uiContext: ProductEditingContext;
  finishStatus: 'pending' | 'success' | 'error' | 'idle';
  blanketStatus: string | undefined;
  autoSaveStatus: 'pending' | 'success' | 'error' | 'idle';
  disabled: boolean;
  disabledReason?: string;
  /**
   * PLT-5109: when true the set is read-only (blanketti). The "Show matching" / "Show attributes"
   * toggles are hidden because the matching/attributes view is hidden entirely; the remaining
   * buttons stay visible but disabled (via `disabled`) with the tooltip.
   */
  readOnly?: boolean;
  companyId?: string;
  /**
   * PLT-4748 PR 2: explicit Edit / Save / Cancel paradigm props. Wired
   * in the DASHBOARD context only — in ONBOARDING the autosave flow
   * stays and these props are unused. `isEditing` toggles which set of
   * controls renders (Edit when false; Save+Cancel when true). When
   * undefined we render the legacy controls (back-compat for any caller
   * that hasn't been updated yet).
   */
  isEditing?: boolean;
  hasUnsavedEdits?: boolean;
  onStartEditing?: () => void;
  onSaveEdits?: () => void;
  onCancelEditing?: () => void;
  saveStatus?: 'pending' | 'success' | 'error' | 'idle';
}

export function ProductControlButtons({
  showMatching,
  onToggleMatching,
  showAttributes,
  onToggleAttributes,
  viewMode,
  onViewModeChange,
  onAddProduct,
  onAddBusinessUnit,
  onClearAll,
  onResetChanges,
  onUpdateClick,
  updateButtonLabel = 'Recompute results',
  uiContext,
  finishStatus,
  blanketStatus,
  autoSaveStatus,
  disabled,
  disabledReason,
  readOnly,
  companyId,
  isEditing,
  hasUnsavedEdits,
  onStartEditing,
  onSaveEdits,
  onCancelEditing,
  saveStatus,
}: ProductControlButtonsProps) {
  const isTeam = useIsTeam();
  // Upright-styled discard confirm (PLT-5707) replacing the two browser
  // `window.confirm` prompts. `null` = no dialog open; otherwise holds the copy
  // and the action to run on confirm. Mirrors the Recompute action's dialog.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    body: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);
  const hasProductAttributeAccess = useHasProductAttributeAccess(companyId);
  // "Show matching" is team-only; "Show attributes" and "Reset changes" follow the attribute rule
  // (`useHasProductAttributeAccess`).
  // On a read-only (blanketti) set the matching/attributes view is hidden entirely, so the two toggles that switch
  // it on are hidden too (PLT-5109). "Reset changes" / "Clear all" remain visible but disabled with the tooltip.
  const showMatchingControl = isTeam && !readOnly;
  const showAttributesControl = (isTeam || hasProductAttributeAccess) && !readOnly;
  const showResetControl = isTeam || hasProductAttributeAccess;

  // PLT-4748 PR 2: Edit / Save / Cancel paradigm is wired in the
  // Dashboard context only. The Onboarding wizard stays in implicit
  // edit mode (autosave) and does not surface Edit/Save/Cancel.
  const editPropsWired = onStartEditing !== undefined && onSaveEdits !== undefined && onCancelEditing !== undefined;
  const editModeActive = uiContext === ProductEditingContext.DASHBOARD && editPropsWired && isEditing === true;
  const showEditButton = uiContext === ProductEditingContext.DASHBOARD && editPropsWired && isEditing === false;

  // In Dashboard view-only mode (not editing), the edit affordances
  // collapse to a single "Edit" entry point — Add product, the kebab
  // menu, and Recompute results are hidden. Recompute remains a
  // post-Save action that the user reaches through the page-level
  // "Recompute results" button at the bottom of the surface (in
  // SetUpProductsMain). The view-mode segmented control stays visible
  // because switching between List and Hierarchy is read-only.
  const showLegacyEditAffordances = uiContext !== ProductEditingContext.DASHBOARD || editModeActive || !editPropsWired;

  // When the original variant is frozen, destructive actions ("Reset changes", "Clear all") are
  // disabled, but the menu itself stays openable so non-destructive, read-only toggles
  // ("Show matching", "Show attributes") remain usable.
  const destructiveItemTitle = disabled ? disabledReason : undefined;

  // View-switch confirm: if the user is mid-edit and tries to switch
  // between List and Hierarchy with unsaved changes, ask before
  // discarding. The view-mode preference itself doesn't lose work (the
  // working set lives in `companyData`, not in viewMode), but PLT-4748
  // spec asks for "block & confirm on view switch" to match the
  // FS-input pattern — interpreted here as a single confirm rather than
  // a hard block, since the user can always save/cancel first.
  const handleViewModeChange = (next: SegmentViewMode) => {
    if (next === viewMode) return;
    if (editModeActive && hasUnsavedEdits === true) {
      setPendingConfirm({
        title: 'Discard unsaved changes?',
        body: 'Switching views will discard your unsaved changes to the product set. Continue?',
        confirmLabel: 'Discard & switch',
        onConfirm: () => {
          // Discard via cancel so the baseline is restored before we change the
          // view. `editModeActive` narrows `onCancelEditing` to defined here
          // (aliased-condition narrowing via `editPropsWired`).
          onCancelEditing();
          onViewModeChange?.(next);
        },
      });
      return;
    }
    // The segmented control is only rendered when `onViewModeChange` is
    // provided, so this is always defined at call time; the optional-call
    // keeps TypeScript happy without an extra guard.
    onViewModeChange?.(next);
  };

  // Cancel-with-confirm: if there are unsaved edits, ask before
  // discarding. Mirrors the FS-input pattern (PR #6875).
  const handleCancelEditing = () => {
    if (!onCancelEditing) return;
    if (hasUnsavedEdits === true) {
      setPendingConfirm({
        title: 'Discard unsaved changes?',
        body: 'You have unsaved changes to the product set. Discard them and exit edit mode?',
        confirmLabel: 'Discard changes',
        onConfirm: onCancelEditing,
      });
      return;
    }
    onCancelEditing();
  };

  const isSaving = saveStatus === 'pending';

  return (
    <>
      <Root>
        {uiContext === ProductEditingContext.DASHBOARD && !editPropsWired && (
          // Back-compat path for any caller that hasn't been updated to
          // wire the Edit / Save / Cancel handlers yet — keep the legacy
          // Recompute button visible so the surface stays usable. New
          // callers wire the handlers and reach Recompute via the
          // page-level button instead.
          <ButtonWithTooltipWhenDisabled
            leadingVisual={<UpdateIcon />}
            variant="primary"
            onClick={onUpdateClick}
            loading={finishStatus === 'pending'}
            disabled={
              finishStatus === 'pending' || blanketStatus !== 'draft' || autoSaveStatus === 'pending' || disabled
            }
            disabledReason={disabled ? disabledReason : undefined}
            id={TrackingId.RECOMPUTE_RESULTS_BUTTON}
          >
            {updateButtonLabel}
          </ButtonWithTooltipWhenDisabled>
        )}
        {showEditButton && (
          <ButtonWithTooltipWhenDisabled
            variant="primary"
            leadingVisual={PencilIcon}
            onClick={onStartEditing}
            disabled={disabled}
            disabledReason={disabledReason}
          >
            Edit products
          </ButtonWithTooltipWhenDisabled>
        )}
        {showLegacyEditAffordances && (
          <>
            {/* "+ Add segment" sits before "+ Add product" as a first-class toolbar button
              (PLT-5707) rather than a buried kebab item. Only the gated segment editor wires
              `onAddBusinessUnit`; other callers omit it and show no button. */}
            {onAddBusinessUnit && (
              <AddButton
                label="Add segment"
                guidance={INPUT_GUIDANCE.addSegment}
                disabled={disabled}
                disabledReason={disabledReason}
                onClick={onAddBusinessUnit}
              />
            )}
            <AddButton
              label="Add product"
              guidance={INPUT_GUIDANCE.addProduct}
              disabled={disabled}
              disabledReason={disabledReason}
              onClick={onAddProduct}
            />
            {/* Cancel / Save sit between "+ Add product" and the kebab, shown only while editing
              (PLT-5799). */}
            {editModeActive && (
              <>
                <Button variant="default" onClick={handleCancelEditing} disabled={isSaving}>
                  Cancel
                </Button>
                <ButtonWithTooltipWhenDisabled
                  variant="primary"
                  leadingVisual={isSaving ? <Spinner size="small" /> : undefined}
                  onClick={onSaveEdits}
                  disabled={isSaving || disabled || hasUnsavedEdits === false}
                  disabledReason={
                    disabled ? disabledReason : hasUnsavedEdits === false ? 'No changes to save' : undefined
                  }
                >
                  Save
                </ButtonWithTooltipWhenDisabled>
              </>
            )}
            <ActionMenu>
              <ActionMenu.Anchor>
                <Button variant="default" aria-label="More options">
                  <KebabHorizontalIcon />
                </Button>
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  {showMatchingControl && (
                    <ActionList.Item onSelect={onToggleMatching}>
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={showMatching} aria-label="Show matching" />
                      </ActionList.LeadingVisual>
                      Show matching
                    </ActionList.Item>
                  )}
                  {showAttributesControl && (
                    <ActionList.Item onSelect={onToggleAttributes}>
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={showAttributes} aria-label="Show attributes" />
                      </ActionList.LeadingVisual>
                      Show attributes
                    </ActionList.Item>
                  )}
                  {onViewModeChange && (
                    <ActionList.Item
                      onSelect={() => {
                        handleViewModeChange(viewMode === 'list' ? 'hierarchy' : 'list');
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={viewMode === 'list'} aria-label="Show flat list" />
                      </ActionList.LeadingVisual>
                      Show flat list
                    </ActionList.Item>
                  )}
                  {showResetControl && (
                    <ActionList.Item onSelect={onResetChanges} disabled={disabled} title={destructiveItemTitle}>
                      <ActionList.LeadingVisual>
                        <ResetIcon />
                      </ActionList.LeadingVisual>
                      Reset changes
                    </ActionList.Item>
                  )}
                  <ActionList.Item
                    onSelect={onClearAll}
                    variant="danger"
                    disabled={disabled}
                    title={destructiveItemTitle}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Clear all
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </>
        )}
        {/* View-only kebab in Dashboard view-only mode — still surfaces
          the team/enterprise-gated read-only toggles. Destructive items
          are hidden because they're edit affordances. */}
        {uiContext === ProductEditingContext.DASHBOARD &&
          editPropsWired &&
          !editModeActive &&
          (showMatchingControl || showAttributesControl || onViewModeChange !== undefined) && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <Button variant="default" aria-label="More options">
                  <KebabHorizontalIcon />
                </Button>
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  {showMatchingControl && (
                    <ActionList.Item onSelect={onToggleMatching}>
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={showMatching} aria-label="Show matching" />
                      </ActionList.LeadingVisual>
                      Show matching
                    </ActionList.Item>
                  )}
                  {showAttributesControl && (
                    <ActionList.Item onSelect={onToggleAttributes}>
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={showAttributes} aria-label="Show attributes" />
                      </ActionList.LeadingVisual>
                      Show attributes
                    </ActionList.Item>
                  )}
                  {onViewModeChange && (
                    <ActionList.Item
                      onSelect={() => {
                        handleViewModeChange(viewMode === 'list' ? 'hierarchy' : 'list');
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <input type="checkbox" checked={viewMode === 'list'} aria-label="Show flat list" />
                      </ActionList.LeadingVisual>
                      Show flat list
                    </ActionList.Item>
                  )}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
      </Root>
      {pendingConfirm && (
        <Dialog
          returnFocusRef={{ current: null }}
          onClose={() => {
            setPendingConfirm(null);
          }}
          title={pendingConfirm.title}
          footerButtons={[
            {
              content: 'Cancel',
              buttonType: 'default',
              onClick: () => {
                setPendingConfirm(null);
              },
            },
            {
              content: pendingConfirm.confirmLabel,
              buttonType: 'danger',
              onClick: () => {
                const { onConfirm } = pendingConfirm;
                setPendingConfirm(null);
                onConfirm();
              },
            },
          ]}
        >
          <DialogBodyText>{pendingConfirm.body}</DialogBodyText>
        </Dialog>
      )}
    </>
  );
}

interface AddButtonProps {
  label: string;
  guidance: string;
  disabled: boolean;
  disabledReason?: string;
  onClick: () => void;
}

// "+ Add segment" / "+ Add product" with a hover/focus guidance tooltip (PLT-7027). While disabled
// the button keeps its disabled-reason tooltip instead, so the two never stack.
// `ButtonWithTooltipWhenDisabled` doesn't forward its ref, which `isNonFocusableTarget` needs, so
// the enabled state renders the Primer button directly.
function AddButton({ label, guidance, disabled, disabledReason, onClick }: AddButtonProps) {
  if (disabled) {
    return (
      <ButtonWithTooltipWhenDisabled
        variant="default"
        leadingVisual={PlusIcon}
        disabled
        disabledReason={disabledReason}
        onClick={onClick}
      >
        {label}
      </ButtonWithTooltipWhenDisabled>
    );
  }
  return (
    <Tooltip text={guidance} placement="top" isNonFocusableTarget>
      <Button variant="default" leadingVisual={PlusIcon} onClick={onClick}>
        {label}
      </Button>
    </Tooltip>
  );
}

const Root = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
`;

const DialogBodyText = styled(Dialog.Body)`
  font-size: var(--font-size__md);
  line-height: 1.5;
`;
